/**
 * Fundo virtual (MediaPipe Selfie Segmenter) — recorta a pessoa do vídeo da câmera e a coloca sobre uma imagem.
 * Tudo roda no navegador: o fundo original nunca sai do computador do médico.
 *
 * Arquivos servidos por public/ (sem CDN):
 *   /mediapipe/wasm/vision_wasm_internal.{js,wasm}  — runtime (copiado de node_modules/@mediapipe/tasks-vision/wasm)
 *   /mediapipe/selfie_segmenter.tflite              — modelo
 * Trocar o fundo = substituir o arquivo da imagem (mesmo nome) em public/backgrounds.
 */
export const DEFAULT_BACKGROUND = `${import.meta.env.BASE_URL}backgrounds/consultorio-azanas.jpg`;

const WASM_PATH = `${import.meta.env.BASE_URL}mediapipe/wasm`;
const MODEL_PATH = `${import.meta.env.BASE_URL}mediapipe/selfie_segmenter.tflite`;
const FPS = 30;

export interface VirtualBackground {
  /** Vídeo já com o fundo trocado + as faixas de áudio originais. */
  stream: MediaStream;
  /** Para o processamento (não encerra as faixas da câmera — quem chamou continua dono delas). */
  stop: () => void;
}

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Imagem de fundo não carregou'));
  img.src = src;
});

/** Desenha `img` preenchendo o canvas (equivale a object-fit: cover). */
function drawCover(ctx: CanvasRenderingContext2D, img: CanvasImageSource, iw: number, ih: number, w: number, h: number) {
  const scale = Math.max(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

/** Timer que não sofre a limitação de abas em segundo plano (o vídeo do médico não pode congelar ao trocar de aba). */
function createTicker(everyMs: number, onTick: () => void) {
  try {
    const url = URL.createObjectURL(new Blob([`setInterval(() => postMessage(0), ${everyMs});`], { type: 'text/javascript' }));
    const worker = new Worker(url);
    worker.onmessage = onTick;
    return () => { worker.terminate(); URL.revokeObjectURL(url); };
  } catch {
    const id = window.setInterval(onTick, everyMs);
    return () => window.clearInterval(id);
  }
}

/** Retorna null se o navegador não suportar (quem chamou segue com a câmera normal). */
export async function createVirtualBackground(camera: MediaStream, backgroundUrl = DEFAULT_BACKGROUND): Promise<VirtualBackground | null> {
  const camTrack = camera.getVideoTracks()[0];
  if (!camTrack || typeof HTMLCanvasElement.prototype.captureStream !== 'function') return null;

  const [{ FilesetResolver, ImageSegmenter }, background] = await Promise.all([import('@mediapipe/tasks-vision'), loadImage(backgroundUrl)]);
  const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
  const segmenter = await ImageSegmenter.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'GPU' },
    runningMode: 'VIDEO',
    outputConfidenceMasks: true,
    outputCategoryMask: false,
  }).catch(() => ImageSegmenter.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'CPU' },
    runningMode: 'VIDEO',
    outputConfidenceMasks: true,
    outputCategoryMask: false,
  }));

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([camTrack]);
  await video.play();
  const settings = camTrack.getSettings();
  const w = video.videoWidth || settings.width || 1280;
  const h = video.videoHeight || settings.height || 720;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const outCtx = out.getContext('2d')!;
  const person = document.createElement('canvas'); // câmera recortada pela máscara
  person.width = w;
  person.height = h;
  const personCtx = person.getContext('2d')!;
  const maskCanvas = document.createElement('canvas'); // máscara em baixa resolução (alfa = confiança)
  const maskCtx = maskCanvas.getContext('2d')!;

  let lastVideoTime = -1;
  let timestamp = 0;
  let busy = false;
  const render = () => {
    if (busy || video.readyState < 2) return;
    busy = true;
    try {
      // sem quadro novo da câmera → repete o último (mantém o stream vivo)
      if (video.currentTime !== lastVideoTime) {
        lastVideoTime = video.currentTime;
        timestamp = Math.max(timestamp + 1, performance.now());
        const result = segmenter.segmentForVideo(video, timestamp);
        const mask = result.confidenceMasks?.[0];
        if (mask) {
          if (maskCanvas.width !== mask.width || maskCanvas.height !== mask.height) { maskCanvas.width = mask.width; maskCanvas.height = mask.height; }
          const conf = mask.getAsFloat32Array();
          const img = maskCtx.createImageData(mask.width, mask.height);
          for (let i = 0; i < conf.length; i++) {
            img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = 255;
            img.data[i * 4 + 3] = Math.min(255, Math.max(0, (conf[i] - 0.35) / 0.4 * 255)); // limiar suave: borda menos serrilhada
          }
          maskCtx.putImageData(img, 0, 0);

          personCtx.globalCompositeOperation = 'source-over';
          personCtx.clearRect(0, 0, w, h);
          personCtx.drawImage(video, 0, 0, w, h);
          personCtx.globalCompositeOperation = 'destination-in';
          personCtx.filter = 'blur(2px)';
          personCtx.drawImage(maskCanvas, 0, 0, w, h);
          personCtx.filter = 'none';
        }
        result.close();
        drawCover(outCtx, background, background.naturalWidth, background.naturalHeight, w, h);
        outCtx.drawImage(person, 0, 0);
      }
    } catch {
      // quadro perdido: o próximo tick tenta de novo
    } finally {
      busy = false;
    }
  };
  render();
  const stopTicker = createTicker(1000 / FPS, render);

  const stream = out.captureStream(FPS);
  camera.getAudioTracks().forEach((t) => stream.addTrack(t));
  return {
    stream,
    stop: () => {
      stopTicker();
      video.pause();
      video.srcObject = null;
      segmenter.close();
    },
  };
}
